import React, { useState, useEffect, useRef, useCallback } from "react";
import AdminLayout from "../../components/admin/AdminLayout.jsx";
import { apiFetch } from "../../utils/api";
import "./AdminShopCategories.css";

const EMPTY_FORM = {
  name:        "",
  slug:        "",
  description: "",
  active:      1,
};

/* ─────────────────────────────────
   Dot-menu (3 vertical dots)
   ───────────────────────────────── */
function DotMenu({ onEdit, onDelete }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    function handler(e) {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  return (
    <div className="asc-dot-menu" ref={ref}>
      <button
        className="asc-dot-menu__trigger"
        onClick={(e) => { e.stopPropagation(); setOpen((v) => !v); }}
        title="Actions"
      >
        <span /><span /><span />
      </button>
      {open && (
        <div className="asc-dot-menu__dropdown">
          <button onClick={() => { onEdit();   setOpen(false); }}>
            <span className="asc-dot-menu__icon">✏</span> Edit
          </button>
          <button
            className="asc-dot-menu__delete"
            onClick={() => { onDelete(); setOpen(false); }}
          >
            <span className="asc-dot-menu__icon">🗑</span> Delete
          </button>
        </div>
      )}
    </div>
  );
}

/* ─────────────────────────────────
   Status Badge
   ───────────────────────────────── */
function StatusBadge({ active }) {
  const isActive = Number(active) === 1;
  return (
    <span className={`asc-badge ${isActive ? "asc-badge--active" : "asc-badge--inactive"}`}>
      {isActive ? "Active" : "Inactive"}
    </span>
  );
}

/* ═══════════════════════════════════
   CREATE / EDIT MODAL
   ═══════════════════════════════════ */
function FormModal({ mode, category, onClose, onSaved }) {
  const [form,   setForm]   = useState(EMPTY_FORM);
  const [saving, setSaving] = useState(false);
  const [error,  setError]  = useState("");

  /* Populate form when editing */
  useEffect(() => {
    if (mode === "edit" && category) {
      setForm({
        name:        category.name        || "",
        slug:        category.slug        || "",
        description: category.description || "",
        active:      Number(category.active) === 1 ? 1 : 0,
      });
    }
  }, [mode, category]);

  const set = (key, val) => setForm((f) => ({ ...f, [key]: val }));

  /* Auto-slug from name (create only) */
  const handleNameChange = (val) => {
    set("name", val);
    if (mode === "create") {
      set(
        "slug",
        val.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, "")
      );
    }
  };

  const handleSubmit = async () => {
    setError("");
    if (!form.name.trim()) return setError("Category name is required.");

    setSaving(true);
    try {
      const payload = {
        name:        form.name.trim(),
        slug:        form.slug.trim() || form.name.toLowerCase().replace(/\s+/g, "-").replace(/[^a-z0-9-]/g, ""),
        description: form.description.trim() || null,
        active:      form.active,
      };

      if (mode === "create") {
        await apiFetch("/api/admin/shop/categories", {
          method:  "POST",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify(payload),
        });
      } else {
        await apiFetch(`/api/admin/shop/categories/${category.id}`, {
          method:  "PUT",
          headers: { "Content-Type": "application/json" },
          body:    JSON.stringify(payload),
        });
      }
      onSaved();
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="asc-modal-overlay" onClick={onClose}>
      <div className="asc-modal asc-modal--form" onClick={(e) => e.stopPropagation()}>
        <div className="asc-modal__header">
          <div className="asc-modal__title-row">
            <span className="asc-modal__tag">{mode === "create" ? "NEW" : "EDIT"}</span>
            <h2 className="asc-modal__title">
              {mode === "create" ? "Add Category" : "Edit Category"}
            </h2>
          </div>
          <button className="asc-modal__close" onClick={onClose}>✕</button>
        </div>

        <div className="asc-modal__body asc-modal__body--form">
          {error && <div className="asc-modal__error">⚠ {error}</div>}

          {/* ── Section: Details ── */}
          <div className="asc-form-section">
            <h3 className="asc-form-section__title">Category Details</h3>
            <div className="asc-form-grid">
              <div className="asc-form-field asc-form-field--full">
                <label>Category Name <span className="req">*</span></label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => handleNameChange(e.target.value)}
                  placeholder="e.g. Brake Parts"
                />
              </div>
              <div className="asc-form-field asc-form-field--full">
                <label>Slug</label>
                <input
                  type="text"
                  value={form.slug}
                  onChange={(e) => set("slug", e.target.value)}
                  placeholder="auto-generated from name"
                />
                <p className="asc-form-hint">
                  Used in URLs and filters — lowercase letters, numbers and hyphens only.
                </p>
              </div>
              <div className="asc-form-field asc-form-field--full">
                <label>Description</label>
                <textarea
                  className="asc-form-textarea"
                  rows={4}
                  value={form.description}
                  onChange={(e) => set("description", e.target.value)}
                  placeholder="Optional description for this category…"
                />
              </div>
            </div>
          </div>

          {/* ── Section: Options ── */}
          <div className="asc-form-section">
            <h3 className="asc-form-section__title">Options</h3>
            <div className="asc-form-grid asc-form-grid--checks">
              <label className="asc-check">
                <input
                  type="checkbox"
                  checked={Number(form.active) === 1}
                  onChange={(e) => set("active", e.target.checked ? 1 : 0)}
                />
                <span>Active (visible in shop filters)</span>
              </label>
            </div>
          </div>
        </div>

        <div className="asc-modal__footer">
          <button className="asc-btn asc-btn--cancel" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button className="asc-btn asc-btn--save" onClick={handleSubmit} disabled={saving}>
            {saving ? "Saving…" : mode === "create" ? "Create Category" : "Save Changes"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   DELETE MODAL
   ═══════════════════════════════════ */
function DeleteModal({ category, onClose, onDeleted }) {
  const [deleting, setDeleting] = useState(false);
  const [error,    setError]    = useState("");

  const handleDelete = async () => {
    setDeleting(true);
    setError("");
    try {
      await apiFetch(`/api/admin/shop/categories/${category.id}`, { method: "DELETE" });
      onDeleted();
    } catch (err) {
      setError(err.message);
      setDeleting(false);
    }
  };

  return (
    <div className="asc-modal-overlay" onClick={onClose}>
      <div className="asc-modal asc-modal--delete" onClick={(e) => e.stopPropagation()}>
        <div className="asc-delete__icon">🗑</div>
        <h2 className="asc-delete__title">Delete Category?</h2>
        <p className="asc-delete__body">
          You are about to permanently delete{" "}
          <strong>{category?.name}</strong>. All products in this category will
          be unlinked but not deleted. This action cannot be undone.
        </p>
        {error && <div className="asc-modal__error">⚠ {error}</div>}
        <div className="asc-delete__actions">
          <button className="asc-btn asc-btn--cancel" onClick={onClose} disabled={deleting}>
            Cancel
          </button>
          <button className="asc-btn asc-btn--delete" onClick={handleDelete} disabled={deleting}>
            {deleting ? "Deleting…" : "Yes, Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

/* ═══════════════════════════════════
   MAIN PAGE
   ═══════════════════════════════════ */
export default function AdminShopCategories() {
  const [categories, setCategories] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [error,      setError]      = useState("");

  /* Filters */
  const [search,      setSearch]      = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const searchTimer = useRef(null);

  /* Modals */
  const [modal,  setModal]  = useState(null); // null | "create" | "edit" | "delete"
  const [target, setTarget] = useState(null); // selected category

  /* ── Load categories ── */
  const loadCategories = useCallback(() => {
    setLoading(true);
    setError("");

    apiFetch("/api/admin/shop/categories")
      .then((data) => {
        let rows = Array.isArray(data) ? data : [];

        /* Client-side filter — the categories endpoint returns all records */
        if (search.trim()) {
          const q = search.trim().toLowerCase();
          rows = rows.filter(
            (c) =>
              c.name?.toLowerCase().includes(q) ||
              c.slug?.toLowerCase().includes(q) ||
              c.description?.toLowerCase().includes(q)
          );
        }
        if (statusFilter !== "") {
          rows = rows.filter((c) => String(Number(c.active)) === statusFilter);
        }

        setCategories(rows);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  }, [search, statusFilter]);

  useEffect(() => { loadCategories(); }, [loadCategories]);

  /* ── Handlers ── */
  const handleSearchChange = (e) => {
    const val = e.target.value;
    setSearch(val);
    clearTimeout(searchTimer.current);
    searchTimer.current = setTimeout(() => {}, 400); // triggers loadCategories via dependency
  };

  const openModal  = (type, category = null) => { setModal(type); setTarget(category); };
  const closeModal = () => { setModal(null); setTarget(null); };
  const afterSave  = () => { closeModal(); loadCategories(); };

  /* ── Derived stats ── */
  const totalActive   = categories.filter((c) => Number(c.active) === 1).length;
  const totalInactive = categories.filter((c) => Number(c.active) !== 1).length;

  return (
    <AdminLayout pageTag="SHOP" pageTitle="Categories">

      {error && (
        <div className="asc-error-bar">⚠ {error}</div>
      )}

      {/* ── Summary strip ── */}
      <div className="asc-summary">
        <div className="asc-summary__item">
          <span className="asc-summary__value">{loading ? "…" : categories.length}</span>
          <span className="asc-summary__label">Shown</span>
        </div>
        <div className="asc-summary__divider" />
        <div className="asc-summary__item">
          <span className="asc-summary__value asc-summary__value--active">{loading ? "…" : totalActive}</span>
          <span className="asc-summary__label">Active</span>
        </div>
        <div className="asc-summary__divider" />
        <div className="asc-summary__item">
          <span className="asc-summary__value asc-summary__value--inactive">{loading ? "…" : totalInactive}</span>
          <span className="asc-summary__label">Inactive</span>
        </div>
      </div>

      {/* ── Toolbar ── */}
      <div className="asc-toolbar">
        <div className="asc-toolbar__left">
          <div className="asc-search-wrap">
            <span className="asc-search-wrap__icon">🔍</span>
            <input
              className="asc-search"
              type="text"
              placeholder="Search categories…"
              value={search}
              onChange={handleSearchChange}
            />
            {search && (
              <button className="asc-search-wrap__clear" onClick={() => setSearch("")}>
                ✕
              </button>
            )}
          </div>

          <select
            className="asc-filter-select"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="">All Statuses</option>
            <option value="1">Active</option>
            <option value="0">Inactive</option>
          </select>
        </div>

        <div className="asc-toolbar__right">
          <button className="asc-btn-primary" onClick={() => openModal("create")}>
            + Add Category
          </button>
        </div>
      </div>

      {/* ── Table ── */}
      {loading ? (
        <div className="asc-table-skeleton" />
      ) : categories.length === 0 ? (
        <div className="asc-empty">
          <span>🗂</span>
          <p>No categories found.</p>
          {(search || statusFilter) && (
            <button
              className="asc-btn asc-btn--cancel"
              onClick={() => { setSearch(""); setStatusFilter(""); }}
            >
              Clear filters
            </button>
          )}
        </div>
      ) : (
        <div className="asc-table-wrap">
          <table className="asc-table">
            <thead>
              <tr>
                <th>#</th>
                <th>Name</th>
                <th>Slug</th>
                <th>Description</th>
                <th>Products</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {categories.map((c, idx) => (
                <tr key={c.id}>
                  <td className="asc-table__id">{c.id}</td>
                  <td>
                    <span className="asc-name-cell">{c.name}</span>
                  </td>
                  <td>
                    <code className="asc-slug">{c.slug}</code>
                  </td>
                  <td>
                    <span className="asc-desc-cell">
                      {c.description
                        ? c.description.length > 80
                          ? c.description.slice(0, 80) + "…"
                          : c.description
                        : <span className="asc-muted">—</span>}
                    </span>
                  </td>
                  <td>
                    <span className="asc-count-badge">
                      {c.product_count ?? 0}
                    </span>
                  </td>
                  <td>
                    <StatusBadge active={c.active} />
                  </td>
                  <td>
                    <DotMenu
                      onEdit={()   => openModal("edit",   c)}
                      onDelete={() => openModal("delete", c)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* ── Modals ── */}
      {modal === "create" && (
        <FormModal mode="create" onClose={closeModal} onSaved={afterSave} />
      )}
      {modal === "edit" && (
        <FormModal mode="edit" category={target} onClose={closeModal} onSaved={afterSave} />
      )}
      {modal === "delete" && (
        <DeleteModal category={target} onClose={closeModal} onDeleted={afterSave} />
      )}

    </AdminLayout>
  );
}